import { useState, useEffect } from "react"
import {
  IconBell,
  IconPlus,
  IconPencil,
  IconTrash,
  IconHistory,
  IconPlayerPause,
  IconPlayerPlay,
  IconDots,
} from "@tabler/icons-react"
import { LuUsers, LuSearch, LuCheck, LuMinus, LuClock } from "react-icons/lu"
import { Button } from "@/components/ui/button"
import { DataTable, type DataTableColumn } from "@/components/ui/data-table"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
} from "@/components/Modal"
import { Input as CustomInput } from "@/components/Input"
import { Label } from "@/components/ui/label"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { LuChevronDown } from "react-icons/lu"
import { Checkbox } from "@/components/ui/checkbox"
import { toast } from "sonner"
import {
  useNotifications,
  useCreateNotification,
  useUpdateNotification,
  useDeleteNotification,
  useNotificationHistory,
  useTeamMembers,
  useModels,
} from "@/data/hooks/api"
import { ApiError } from "@/data/api/client"
import type {
  NotificationConfiguration,
  TimePeriod,
} from "@/data/types/alerts.types"
import type { TeamMember } from "@/data/types/team.types"
import type { MetadataFilter } from "@/data/types/analytics.types"
import type { ModelSelection } from "@/data/types/common.types"
import { ALL_MODELS_SELECTION } from "@/components/ModelsSelector"
import { useAuth } from "@/hooks/useAuth"
import { PageContainer } from "@/components/PageContainer"
import { PageHeader } from "@/components/PageHeader"
import { UpgradeBanner } from "@/components/UpgradeBanner"
import { Chip } from "@/components/Chip"
import { ListTooltip, type ListTooltipItem } from "@/components/ListTooltip"
import { cn } from "@/lib/utils"
import { ModelsSelector } from "@/components/ModelsSelector"
import { InlineMetadataFilters, type AppliedFilter } from "@/components/FiltersSelector"
import { ButtonSelect } from "@/components/ButtonSelect"

const TIME_PERIOD_OPTIONS: { value: TimePeriod; label: string }[] = [
  { value: "last_hour", label: "Last Hour" },
  { value: "last_24_hours", label: "Last 24 Hours" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
]

const getTimePeriodLabel = (period: TimePeriod): string => {
  return TIME_PERIOD_OPTIONS.find((opt) => opt.value === period)?.label || period
}

interface RecipientsSelectorProps {
  value: string[]
  onChange: (userIds: string[]) => void
  members: TeamMember[]
  loading: boolean
}

function RecipientsSelector({ value, onChange, members, loading }: RecipientsSelectorProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  const selectedMembers = members.filter((m) => value.includes(m.id))
  const selectedCount = selectedMembers.length

  const filteredMembers = searchQuery
    ? members.filter(
      (m) =>
        m.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.email.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : members

  const isAllSelected = members.length > 0 && selectedCount === members.length
  const isNoneSelected = selectedCount === 0

  const handleToggleMember = (userId: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (!userId) return // Prevent adding null/undefined IDs

    if (value.includes(userId)) {
      onChange(value.filter((id) => id !== userId))
    } else {
      onChange([...value, userId])
    }
  }

  const handleSelectAll = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onChange(members.map((m) => m.id).filter(Boolean))
  }

  const handleClearAll = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onChange([])
  }

  const getDisplayText = () => {
    if (loading) return "Loading..."
    if (isAllSelected) return "All Recipients"
    if (isNoneSelected) return "No Recipients"
    return `${selectedCount} Recipient${selectedCount === 1 ? "" : "s"}`
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="transparent-outline"
          size="xs"
          disabled={loading}
          className={cn(
            "hover:bg-[#F2EEFF] hover:text-[#654EDA] hover:border-[#E2DAFF]",
            "focus-visible:ring-[#654EDA]/20 focus-visible:border-[#654EDA] focus-visible:ring-2",
            open && "bg-[#E2DAFF] text-[#654EDA] border-[#C6B9FF] hover:bg-[#E2DAFF] focus-visible:ring-0"
          )}
        >
          <LuUsers className="size-4" />
          {getDisplayText()}
          <LuChevronDown
            className={cn(
              "size-3.5 ml-0.5 transition-transform duration-200",
              open && "rotate-180"
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[320px] p-0 border-[#EDEEF3] shadow-lg flex flex-col max-h-[var(--radix-popover-content-available-height,400px)]"
        align="start"
        sideOffset={6}
      >
        {/* Header with Search and Clear */}
        <div className="p-3 border-b border-[#EDEEF3] shrink-0">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#8A8F98]" />
              <input
                type="text"
                placeholder="Search recipients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-[30px] pl-9 pr-3 rounded-[6px] border border-[#EDEEF3] bg-white text-[13px] text-[#08090A] placeholder:text-[#8A8F98] hover:border-[#D1D5DB] focus:outline-none focus:border-[#654EDA] transition-colors"
              />
            </div>
            {/* Clear button - shown when there are selections */}
            {!loading && selectedCount > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                className="text-[12px] text-[#8A8F98] hover:text-[#654EDA] transition-colors whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="p-4 text-center text-[13px] text-[#8A8F98]">
              Loading recipients...
            </div>
          ) : members.length === 0 ? (
            <div className="p-4 text-center text-[13px] text-[#8A8F98]">
              No team members available
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="p-4 text-center text-[13px] text-[#8A8F98]">
              No recipients found
            </div>
          ) : (
            <>
              {/* Select All Option */}
              {!searchQuery && (
                <div
                  className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#F7F8F8] transition-colors border-b border-[#EDEEF3]"
                  onClick={handleSelectAll}
                >
                  <div className="relative">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={() => handleSelectAll()}
                      className={cn(
                        "size-4 border-[#D1D5DB] data-[state=checked]:bg-[#654EDA] data-[state=checked]:border-[#654EDA]",
                        !isAllSelected && !isNoneSelected && "data-[state=unchecked]:bg-[#654EDA]/20 data-[state=unchecked]:border-[#654EDA]"
                      )}
                    />
                    {!isAllSelected && !isNoneSelected && (
                      <LuMinus className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-3 text-[#654EDA]" />
                    )}
                  </div>
                  <LuUsers className="size-4 text-[#8A8F98]" />
                  <span className="text-[13px] font-medium text-[#08090A]">
                    Select All
                  </span>
                  <span className="ml-auto text-[12px] text-[#8A8F98]">
                    {selectedCount}/{members.length}
                  </span>
                </div>
              )}

              {/* Recipients List */}
              {filteredMembers.map((member) => {
                const isSelected = value.includes(member.id)
                return (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#F7F8F8] transition-colors border-b border-[#EDEEF3] last:border-b-0"
                    onClick={(e) => handleToggleMember(member.id, e)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleToggleMember(member.id)}
                      className="size-4 border-[#D1D5DB] data-[state=checked]:bg-[#654EDA] data-[state=checked]:border-[#654EDA]"
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-[13px] text-[#08090A] truncate">
                        {member.first_name} {member.last_name}
                      </span>
                      <span className="text-[11px] text-[#8A8F98] truncate">
                        {member.email}
                      </span>
                    </div>
                    {isSelected && (
                      <LuCheck className="size-4 text-[#654EDA] shrink-0" />
                    )}
                  </div>
                )
              })}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// Operation labels for display
const OPERATION_LABELS: Record<string, string> = {
  is: "is",
  is_not: "is not",
  like: "like",
  not_like: "not like",
}

// Convert AppliedFilter to MetadataFilter for API
const appliedFiltersToMetadata = (filters: AppliedFilter[]): MetadataFilter[] => {
  return filters.map((f) => ({
    key: f.tagName,
    operator: f.operation === "is" ? "eq" as const : "neq" as const,
    value: f.value,
  }))
}

// Convert MetadataFilter to AppliedFilter for form
const metadataToAppliedFilters = (filters: MetadataFilter[] | null): AppliedFilter[] => {
  if (!filters) return []
  return filters.map((f) => {
    const operationValue = f.operator === "eq" ? "is" : f.operator === "neq" ? "is_not" : f.operator === "like" ? "like" : "not_like"
    return {
      id: crypto.randomUUID(),
      tagName: f.key,
      tagNameLabel: f.key, // Use the key as label (fetched from API anyway)
      operation: operationValue,
      operationLabel: OPERATION_LABELS[operationValue] || operationValue,
      value: f.value,
    }
  })
}

export function AlertsPage() {
  const { organization, isOnFreePlan } = useAuth()
  const canManage = organization?.role === "OWNER" || organization?.role === "ADMIN"

  // Pagination state
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const { data: notificationsData, isLoading: loading } = useNotifications({
    limit: pageSize,
    offset: pageIndex * pageSize,
  })
  const { data: teamMembersData, isLoading: loadingMembers } = useTeamMembers()
  const createNotification = useCreateNotification()
  const updateNotification = useUpdateNotification()
  const deleteNotification = useDeleteNotification()

  // Fetch models for default selection
  const { data: modelGroups } = useModels()
  const allModels = modelGroups ? modelGroups.flatMap((g) => g.models) : []

  const notifications = notificationsData?.items ?? []
  const totalItems = notificationsData?.total ?? 0
  const teamMembers = (teamMembersData?.members || []).map((m: any) => ({
    ...m,
    user_id: m.id,
  }))

  // Create/Edit dialog state
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)
  const [editingNotification, setEditingNotification] = useState<NotificationConfiguration | null>(null)

  // Form state
  const [formName, setFormName] = useState("")
  const [formDescription, setFormDescription] = useState("")
  const [formCostThreshold, setFormCostThreshold] = useState("")
  const [formTimePeriod, setFormTimePeriod] = useState<TimePeriod>("last_hour")
  const [formThrottleHours, setFormThrottleHours] = useState("1")
  const [formModels, setFormModels] = useState<ModelSelection>(ALL_MODELS_SELECTION)
  const [formMetadataFilters, setFormMetadataFilters] = useState<AppliedFilter[]>([])
  const [formRecipients, setFormRecipients] = useState<string[]>([])
  const [formIsEnabled, setFormIsEnabled] = useState(true)

  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deletingNotification, setDeletingNotification] = useState<NotificationConfiguration | null>(null)

  // History dialog state
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false)
  const [historyNotification, setHistoryNotification] = useState<NotificationConfiguration | null>(null)
  const { data: historyData, isLoading: loadingHistory } = useNotificationHistory(
    historyNotification?.id || null,
    50,
    0
  )
  const historyItems = historyData?.items || []

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setPageIndex(0) // Reset to first page when page size changes
  }

  // Auto-select all models when dialog opens for new alert (not when editing)
  useEffect(() => {
    if (isFormDialogOpen && !editingNotification && allModels.length > 0 && !formModels.all && formModels.providers.length === 0 && formModels.models.length === 0) {
      setFormModels(ALL_MODELS_SELECTION)
    }
  }, [isFormDialogOpen, allModels.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setFormName("")
    setFormDescription("")
    setFormCostThreshold("")
    setFormTimePeriod("last_hour")
    setFormThrottleHours("1")
    setFormModels(ALL_MODELS_SELECTION) // Reset to all models selected
    setFormMetadataFilters([])
    setFormRecipients([])
    setFormIsEnabled(true)
    setEditingNotification(null)
  }

  const handleOpenCreateDialog = () => {
    resetForm()
    setIsFormDialogOpen(true)
  }

  const handleOpenEditDialog = (notification: NotificationConfiguration) => {
    setEditingNotification(notification)
    setFormName(notification.name)
    setFormDescription(notification.description || "")
    setFormCostThreshold(String(notification.cost_threshold))
    setFormTimePeriod(notification.time_period)
    setFormThrottleHours(String(notification.throttle_hours))
    setFormModels(notification.model_selection || ALL_MODELS_SELECTION)
    setFormMetadataFilters(metadataToAppliedFilters(notification.metadata_filters))
    // Extract user IDs from recipients array
    setFormRecipients(notification.recipients?.map((r) => r.user_id) ?? [])
    setFormIsEnabled(notification.is_enabled)
    setIsFormDialogOpen(true)
  }

  const handleCloseFormDialog = () => {
    setIsFormDialogOpen(false)
    resetForm()
  }

  const handleSaveNotification = async () => {
    if (!formName.trim()) {
      toast.error("Name is required")
      return
    }

    const costThreshold = parseFloat(formCostThreshold)
    if (isNaN(costThreshold) || costThreshold <= 0) {
      toast.error("Cost threshold must be a positive number")
      return
    }

    const throttleHours = parseInt(formThrottleHours, 10)
    if (isNaN(throttleHours) || throttleHours < 0 || throttleHours > 168) {
      toast.error("Minimum time between alerts must be between 0 and 168 hours")
      return
    }

    if (formRecipients.length === 0) {
      toast.error("Please select at least one recipient")
      return
    }

    try {
      const metadataFilters = appliedFiltersToMetadata(formMetadataFilters)
      const notificationData = {
        name: formName,
        description: formDescription || null,
        is_enabled: formIsEnabled,
        model_selection: formModels,
        metadata_filters: metadataFilters.length > 0 ? metadataFilters : null,
        time_period: formTimePeriod,
        cost_threshold: costThreshold,
        throttle_hours: throttleHours,
        recipient_user_ids: formRecipients,
      }

      if (editingNotification) {
        // Update existing notification
        await updateNotification.mutateAsync({
          id: editingNotification.id,
          data: notificationData,
        })
        toast.success("Notification updated successfully")
      } else {
        // Create new notification
        await createNotification.mutateAsync(notificationData)
        toast.success("Notification created successfully")
      }

      handleCloseFormDialog()
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message)
      } else {
        toast.error(editingNotification ? "Failed to update notification" : "Failed to create notification")
      }
    }
  }

  const handleToggleEnabled = async (notification: NotificationConfiguration) => {
    try {
      const updatedNotification = await updateNotification.mutateAsync({
        id: notification.id,
        data: {
          is_enabled: !notification.is_enabled,
        },
      })
      toast.success(`Notification ${updatedNotification.is_enabled ? "enabled" : "disabled"}`)
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message)
      } else {
        toast.error("Failed to update notification")
      }
    }
  }

  const handleOpenDeleteDialog = (notification: NotificationConfiguration) => {
    setDeletingNotification(notification)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteNotification = async () => {
    if (!deletingNotification) return

    try {
      await deleteNotification.mutateAsync(deletingNotification.id)
      setIsDeleteDialogOpen(false)
      setDeletingNotification(null)
      toast.success("Notification deleted successfully")
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message)
      } else {
        toast.error("Failed to delete notification")
      }
    }
  }

  const handleOpenHistoryDialog = (notification: NotificationConfiguration) => {
    setHistoryNotification(notification)
    setIsHistoryDialogOpen(true)
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatCurrency = (value: string | number) => {
    const num = typeof value === "string" ? parseFloat(value) : value
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num)
  }

  // Column definitions for the alerts table
  const alertsTableColumns: DataTableColumn<NotificationConfiguration>[] = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      flex: 2,
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="font-medium text-[#08090A] truncate">{row.original.name}</div>
          {row.original.description && (
            <div className="text-[11px] text-[#8A8F98] truncate">
              {row.original.description}
            </div>
          )}
        </div>
      ),
    },
    {
      id: "cost_threshold",
      header: "Cost Threshold",
      accessorKey: "cost_threshold",
      flex: 1.1,
      cell: ({ row }) => <span className="text-[#08090A]">{formatCurrency(row.original.cost_threshold)}</span>,
    },
    {
      id: "time_period",
      header: "Time Period",
      accessorKey: "time_period",
      flex: 0.85,
      cell: ({ row }) => <span className="text-[#8A8F98]">{getTimePeriodLabel(row.original.time_period)}</span>,
    },
    {
      id: "recipients",
      header: "Recipients",
      flex: 0.8,
      cell: ({ row }) => {
        const recipients = row.original.recipients || []
        const tooltipItems: ListTooltipItem[] = recipients.map(r => ({
          id: r.user_id,
          label: `${r.first_name} ${r.last_name}`,
        }))

        return (
          <ListTooltip
            items={tooltipItems}
            emptyMessage="No recipients"
            side="bottom"
            align="start"
          >
            <div className="cursor-default">
              <Chip
                label={`Recipients (${recipients.length})`}
                icon={<LuUsers className="size-3 text-[#8A8F98]" />}
              />
            </div>
          </ListTooltip>
        )
      }
    },
    {
      id: "status",
      header: "Status",
      flex: 0.7,
      cell: ({ row }) => {
        const isEnabled = row.original.is_enabled
        return (
          <Chip
            label={isEnabled ? "Active" : "Paused"}
            showDot
            dotColor={isEnabled ? "#2ECC71" : "#34495E"}
          />
        )
      },
    },
    {
      id: "actions",
      header: "",
      flex: 0.3,
      enableSorting: false,
      cell: ({ row }) => {
        const notification = row.original
        return (
          <div className="flex items-center justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center justify-center size-[28px] rounded-[4px] text-[#8A8F98] hover:text-[#08090A] hover:bg-[#EDEEF3] transition-colors cursor-pointer"
                >
                  <IconDots className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[160px] p-[4px] rounded-[6px] border-[#EDEEF3] shadow-lg">
                <DropdownMenuItem
                  onClick={() => handleOpenHistoryDialog(notification)}
                  className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#08090A] cursor-pointer"
                >
                  <IconHistory className="mr-[8px] size-3.5 text-[#8A8F98]" />
                  View History
                </DropdownMenuItem>
                {canManage && (
                  <>
                    <DropdownMenuItem
                      onClick={() => handleOpenEditDialog(notification)}
                      className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#08090A] cursor-pointer"
                    >
                      <IconPencil className="mr-[8px] size-3.5 text-[#8A8F98]" />
                      Edit Alert
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleToggleEnabled(notification)}
                      className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#08090A] cursor-pointer"
                    >
                      {notification.is_enabled ? (
                        <>
                          <IconPlayerPause className="mr-[8px] size-3.5 text-[#8A8F98]" />
                          Pause Alert
                        </>
                      ) : (
                        <>
                          <IconPlayerPlay className="mr-[8px] size-3.5 text-[#8A8F98]" />
                          Enable Alert
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-[4px] bg-[#EDEEF3]" />
                    <DropdownMenuItem
                      onClick={() => handleOpenDeleteDialog(notification)}
                      className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#E74C3C] cursor-pointer focus:text-[#E74C3C] focus:bg-[#E74C3C]/5"
                    >
                      <IconTrash className="mr-[8px] size-3.5" />
                      Delete Alert
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      },
    },
  ]

  return (
    <>
      {isOnFreePlan && totalItems >= 1 && (
        <UpgradeBanner type="alerts-limit" />
      )}
      <PageContainer maxWidth="1100px" className="lg:py-[32px]">
        <PageHeader
          title="Cost Alerts"
          description="Set up alerts to get notified when your spending exceeds defined thresholds."
        />

        <div className="space-y-[13px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[18px] font-medium text-[#08090A] px-[4px]">All Alerts</h2>
            {canManage && (
              <Button
                onClick={handleOpenCreateDialog}
                size="sm"
                className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium px-[13px] rounded-[6px]"
              >
                <IconPlus className="size-4" />
                Create Alert
              </Button>
            )}
          </div>
          <DataTable
            data={notifications}
            columns={alertsTableColumns}
            getRowId={(row) => row.id}
            pagination={{
              enabled: true,
              mode: "server",
              pageIndex,
              pageSize,
              totalItems: totalItems,
              onPageChange: setPageIndex,
              onPageSizeChange: handlePageSizeChange,
              pageSizeOptions: [5, 10, 20, 50],
            }}
            emptyMessage="No cost alerts configured yet"
            emptyIcon={<IconBell className="size-6 text-[#8A8F98]" />}
            loading={loading}
          />
        </div>
      </PageContainer>

      {/* Create/Edit Modal */}
      <Modal open={isFormDialogOpen} onOpenChange={handleCloseFormDialog}>
        <ModalContent className="max-w-[520px] max-h-[90vh] overflow-y-auto p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">
              {editingNotification ? "Edit Cost Alert" : "Create Cost Alert"}
            </ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Configure when and how you want to be notified about spending.
            </ModalDescription>
          </ModalHeader>

          <div className="px-[24px] py-[24px] space-y-[16px]">
            {/* Name */}
            <div className="space-y-[6px]">
              <Label htmlFor="notification-name" className="text-[12px] text-[#08090A]">Name *</Label>
              <CustomInput
                id="notification-name"
                size="sm"
                placeholder="e.g., High Spend Alert"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                disabled={createNotification.isPending || updateNotification.isPending}
                className="w-full"
              />
            </div>

            {/* Description */}
            <div className="space-y-[6px]">
              <Label htmlFor="notification-description" className="text-[12px] text-[#08090A]">Description</Label>
              <CustomInput
                id="notification-description"
                size="sm"
                placeholder="Optional description for this alert"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                disabled={createNotification.isPending || updateNotification.isPending}
                className="w-full"
              />
            </div>

            <div className="grid grid-cols-2 gap-[16px]">
              {/* Cost Threshold */}
              <div className="space-y-[6px]">
                <Label htmlFor="cost-threshold" className="text-[12px] text-[#08090A]">Threshold (USD) *</Label>
                <div className="relative">
                  <span className="absolute left-[10px] top-1/2 -translate-y-1/2 text-[13px] text-[#8A8F98]">
                    $
                  </span>
                  <CustomInput
                    id="cost-threshold"
                    size="sm"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="100.00"
                    value={formCostThreshold}
                    onChange={(e) => setFormCostThreshold(e.target.value)}
                    disabled={createNotification.isPending || updateNotification.isPending}
                    className="pl-[20px]"
                  />
                </div>
              </div>

              {/* Time Period */}
              <div className="space-y-[6px]">
                <Label className="text-[12px] text-[#08090A]">Time Period *</Label>
                <ButtonSelect
                  value={formTimePeriod}
                  onValueChange={(v) => setFormTimePeriod(v as TimePeriod)}
                  options={TIME_PERIOD_OPTIONS}
                  icon={<LuClock className="size-4" />}
                  header="Time Period"
                  dropdownWidth={180}
                  size="sm"
                  className="justify-between"
                />
              </div>
            </div>

            {/* Minimum Time Between Alerts */}
            <div className="space-y-[6px]">
              <Label htmlFor="throttle-hours" className="text-[12px] text-[#08090A]">Minimum time between alerts (hours) *</Label>
              <div className="flex items-center gap-[12px]">
                <CustomInput
                  id="throttle-hours"
                  size="sm"
                  type="number"
                  min="0"
                  max="168"
                  placeholder="1"
                  value={formThrottleHours}
                  onChange={(e) => setFormThrottleHours(e.target.value)}
                  disabled={createNotification.isPending || updateNotification.isPending}
                  className="w-[80px]"
                />
                <span className="text-[12px] text-[#8A8F98]">0 to 168 hours (max 7 days)</span>
              </div>
            </div>

            {/* Models Filter */}
            <div className="space-y-[6px]">
              <Label className="text-[12px] text-[#08090A]">Filter by Models</Label>
              <div>
                <ModelsSelector
                  value={formModels}
                  onChange={setFormModels}
                />
              </div>
            </div>

            {/* Metadata Filters */}
            <div className="space-y-[6px]">
              <Label className="text-[12px] text-[#08090A]">Filters</Label>
              <InlineMetadataFilters
                value={formMetadataFilters}
                onChange={setFormMetadataFilters}
                addButtonLabel="Add Filter"
              />
            </div>

            {/* Recipients */}
            <div className="space-y-[6px]">
              <Label className="text-[12px] text-[#08090A]">Recipients *</Label>
              <RecipientsSelector
                value={formRecipients}
                onChange={setFormRecipients}
                members={teamMembers}
                loading={loadingMembers}
              />
            </div>

            {/* Enabled State */}
            <div className="flex items-center space-x-[8px] pt-[4px]">
              <Checkbox
                id="is-enabled"
                checked={formIsEnabled}
                onCheckedChange={(checked) => setFormIsEnabled(checked === true)}
                disabled={createNotification.isPending || updateNotification.isPending}
                className="border-[#EDEEF3]"
              />
              <Label htmlFor="is-enabled" className="text-[12px] text-[#08090A] cursor-pointer">
                Enable this alert immediately
              </Label>
            </div>
          </div>

          <ModalFooter className="justify-end p-[24px] pt-0 gap-[8px]">
            <Button
              variant="transparent-outline"
              onClick={handleCloseFormDialog}
              disabled={createNotification.isPending || updateNotification.isPending}
              size="sm"
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveNotification}
              disabled={createNotification.isPending || updateNotification.isPending}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              {createNotification.isPending || updateNotification.isPending
                ? editingNotification
                  ? "Updating..."
                  : "Creating..."
                : editingNotification
                  ? "Update Alert"
                  : "Create Alert"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Modal */}
      <Modal open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">Delete Alert</ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Are you sure you want to delete the alert <span className="font-semibold text-[#08090A]">{deletingNotification?.name}</span>? This action cannot be undone.
            </ModalDescription>
          </ModalHeader>

          <ModalFooter className="justify-end p-[24px] gap-[8px]">
            <Button
              variant="transparent-outline"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={deleteNotification.isPending}
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleDeleteNotification}
              disabled={deleteNotification.isPending}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              {deleteNotification.isPending ? "Deleting..." : "Delete Alert"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* History Modal */}
      <Modal open={isHistoryDialogOpen} onOpenChange={setIsHistoryDialogOpen}>
        <ModalContent className="max-w-[700px] max-h-[80vh] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">Alert History</ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Showing when "{historyNotification?.name}" was triggered.
            </ModalDescription>
          </ModalHeader>

          <div className="p-[24px] overflow-y-auto">
            {loadingHistory ? (
              <div className="py-8 text-center text-[13px] text-[#8A8F98]">Loading history...</div>
            ) : historyItems.length === 0 ? (
              <div className="py-8 text-center text-[13px] text-[#8A8F98]">
                This alert hasn't been triggered yet.
              </div>
            ) : (
              <div className="rounded-[8px] border border-[#EDEEF3] overflow-hidden">
                <Table>
                  <TableHeader className="bg-[#F7F8F8]">
                    <TableRow className="h-[44px] border-b border-[#EDEEF3]">
                      <TableHead className="text-[12px] font-medium text-[#8A8F98] uppercase tracking-wide px-[16px]">Triggered At</TableHead>
                      <TableHead className="text-[12px] font-medium text-[#8A8F98] uppercase tracking-wide px-[16px]">Cost Value</TableHead>
                      <TableHead className="text-[12px] font-medium text-[#8A8F98] uppercase tracking-wide px-[16px]">Threshold</TableHead>
                      <TableHead className="text-[12px] font-medium text-[#8A8F98] uppercase tracking-wide px-[16px]">Recipients</TableHead>
                      <TableHead className="text-[12px] font-medium text-[#8A8F98] uppercase tracking-wide px-[16px]">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="divide-y divide-[#EDEEF3]">
                    {historyItems.map((item) => (
                      <TableRow key={item.id} className="h-[44px] hover:bg-[#F7F8F8] transition-colors">
                        <TableCell className="text-[13px] text-[#08090A] px-[16px]">{formatDateTime(item.triggered_at)}</TableCell>
                        <TableCell className="text-[13px] text-[#08090A] px-[16px]">{formatCurrency(item.cost_value)}</TableCell>
                        <TableCell className="text-[13px] text-[#08090A] px-[16px]">{formatCurrency(item.cost_threshold)}</TableCell>
                        <TableCell className="text-[13px] text-[#08090A] px-[16px]">{item.recipients_count}</TableCell>
                        <TableCell className="text-[13px] px-[16px]">
                          {item.email_sent ? (
                            <Chip
                              label="Sent"
                              showDot
                              dotColor="#2ECC71"
                            />
                          ) : (
                            <Chip
                              label="Failed"
                              showDot
                              dotColor="#E74C3C"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <ModalFooter className="justify-end p-[24px] pt-0">
            <Button
              onClick={() => setIsHistoryDialogOpen(false)}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </>
  )
}
